
import React, { Component, createRef } from 'react';
import "./textarea.css";

class InputText extends Component {
  textareaRef = createRef();

  componentDidMount() {
    // Resize once after initial mount
    this.resizeIfNeeded();
  }

  componentDidUpdate(prevProps) {
    // If value changes externally, re-measure and resize
    if (this.props.autoResize) {
      const valueChanged =
        this.props.value !== prevProps.value ||
        this.props.defaultValue !== prevProps.defaultValue;

      if (valueChanged) {
        this.resizeIfNeeded();
      }
    }
  }

  resizeIfNeeded = () => {
    if (!this.props.autoResize) return;
    const el = this.textareaRef.current;
    if (!el) return;

    // Respect readOnly; you might still want to auto-size even if readOnly
    // so we won't block it here.

    // Reset height to measure scrollHeight accurately
    el.style.height = 'auto';

    // Optional row-based min/max control
    const { minRows, maxRows } = this.props;
    const lineHeight = this.getComputedLineHeight(el); // px per line

    // Calculate desired height
    const desired = el.scrollHeight;

    // Constrain to min/max rows if provided
    let finalHeight = desired;
    if (minRows && lineHeight) {
      finalHeight = Math.max(finalHeight, minRows * lineHeight);
    }
    if (maxRows && lineHeight) {
      finalHeight = Math.min(finalHeight, maxRows * lineHeight);
      // If content exceeds max, allow vertical scroll
      el.style.overflowY = desired > finalHeight ? 'auto' : 'hidden';
    } else {
      el.style.overflowY = 'hidden';
    }

    el.style.height = `${finalHeight}px`;
  };

  getComputedLineHeight = (el) => {
    // Reads computed line-height (usually in px)
    const lh = window.getComputedStyle(el).lineHeight;
    // Some browsers return 'normal'—fallback to 20px or any sensible default
    if (!lh || lh === 'normal') return 20;
    const parsed = parseFloat(lh);
    return Number.isFinite(parsed) ? parsed : 20;
  };

  handleInput = (e) => {
    // Fire parent onChange first to keep controlled values in sync
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(e);
    }
    // Then resize
    this.resizeIfNeeded();
  };

  render() {
    const {
      id,
      label,
      labelSuffix,
      style,
      placeholder,
      rows,
      readOnly,
      value,
      defaultValue,
      className = "",
    } = this.props;

    // Combine your existing classes with recommended ones
    const textareaClass = `form-control fullsize ${className}`.trim();

    return (
      <div className="row form-group justify-content-start">
        {label && (
          <label style={style} className="col-12 col-form-label">
            {label}&nbsp;{labelSuffix}
          </label>
        )}
        <div className="col-12">
          <textarea
            id={id}
            ref={this.textareaRef}
            // Controlled vs uncontrolled support:
            // If parent passes 'value', treat as controlled; else use defaultValue
            {...(value !== undefined ? { value } : { defaultValue })}
            onChange={this.handleInput}
            onInput={this.handleInput}   // ensures resizing while typing
            className={textareaClass}
            placeholder={placeholder}
            rows={rows}
            readOnly={readOnly}
            style={{
              // Ensure no manual resize handle; we control height programmatically
              resize: 'none',
              // We toggle overflowY during resize; default hidden here:
              overflowY: 'hidden',
              // Let width fill its container
              width: '100%',
            }}
          />
        </div>
      </div>
    );
  }
}

export default InputText;